import './PublicShareHeader.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, ButtonProps, Input, Popup } from 'semantic-ui-react';
import moment from 'moment';
import { useAppSelector } from '../../hooks/ReduxHooks';
import { DataLayoutPosition, DataLayoutTab, FieldPosition, TemplatePage } from '../../types/template-types';
import { useDispatch } from 'react-redux';
import { updateSavedPublicFormState } from '../actions';
import ValidationErrorsPopup from './FieldValidations/ValidationErrorsPopup';
import { canIUse, Features } from '../../helpers/PermissionUtility';
import RequiredFieldDropdown from './RequiredFieldDropdown';

export interface PublicShareFormHeaderProps {
  isSaving?: boolean;
  isFetching?: boolean;
  confirmSubmitSharedForm?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: ButtonProps) => void;
  initialCalculationsHaveRun?: boolean;
  bringToFieldPosition: (page: TemplatePage, position: FieldPosition) => void;
  bringToDataLayoutPosition: (tab: DataLayoutTab, position: DataLayoutPosition) => void;
  allowSave: boolean;
  sharedAccessToken: string;
  savePublicForm: () => void;
}

export default function PublicShareFormHeader(props: PublicShareFormHeaderProps) {
  const dispatch = useDispatch();
  const savedPublicForm = useAppSelector((state) => state.formeditor.savedPublicForm);
  const recipientEmailAddress = useAppSelector((state) => state.formeditor.policyInfo?.recipient?.email);
  const savedEmailAddress = useAppSelector((state) => state.formeditor.savedPublicForm?.emailAddress);
  const hasMissingRequiredFields = useAppSelector((state) => state.formeditor.missingRequiredFieldIds)?.length > 0;
  const loading = useAppSelector((state) => state.formeditor.isPublicFormSaving);

  const [savePopupOpen, setSavePopupOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [validEmail, setValidEmail] = useState<boolean>(false);
  const [popupPosition, setPopupPosition] = useState<'top right' | 'top center'>('top right');

  const savePublicForm = useCallback(() => {
    if (emailAddress) {
      dispatch(
        updateSavedPublicFormState({
          emailAddress: emailAddress
        })
      );
      props.savePublicForm();
    }
  }, [emailAddress, dispatch, props]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        event.preventDefault();
        if (!savedPublicForm?.saveId) {
          setSavePopupOpen(true);
        } else {
          savePublicForm();
        }
      }

      if (event.key === 'Enter' && validEmail && savePopupOpen) {
        event.preventDefault();
        savePublicForm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [savePublicForm, savedPublicForm, validEmail, savePopupOpen]);

  useEffect(() => {
    if (savedPublicForm?.saveId && window.location.pathname !== `/s/saved/${savedPublicForm.saveId}`) {
      history.replaceState(null, null, `/s/saved/${savedPublicForm.saveId}`);
      setSavePopupOpen(false);
    }
  }, [savedPublicForm]);

  useEffect(() => {
    if (savedEmailAddress) {
      setEmailAddress(savedEmailAddress);
      setValidEmail(true);
      return;
    }

    if (recipientEmailAddress) {
      setEmailAddress(recipientEmailAddress);
      setValidEmail(true);
    }
  }, [recipientEmailAddress, savedEmailAddress]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 630) {
        setPopupPosition('top center');
      } else {
        setPopupPosition('top right');
      }
    };

    handleResize(); // Set initial position
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onEmailAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value);

    if (!e.target.value) {
      setEmailError(false);
      setValidEmail(false);
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmail = emailPattern.test(e.target.value);
    if (e.target.value && validEmail) {
      setEmailError(false);
      setValidEmail(true);
    }

    if (e.target.value && !validEmail) {
      setValidEmail(false);
    }
  };

  const validateEmailAddress = () => {
    if (emailAddress) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(!emailPattern.test(emailAddress));
    }
  };

  return (
    <div className="ui secondary fitted menu publicshare-header">
      <div className="item publicshare-title">
        <div>Turn your paper forms into smart forms for mobile and web</div>
        <a href="https://www.goformz.com/content/pf-signup" target="_blank">
          Learn more
        </a>
      </div>
      <div className="right menu">
        {hasMissingRequiredFields && canIUse(Features.FieldValidations) ? (
          <ValidationErrorsPopup bringToFieldPosition={props.bringToFieldPosition} bringToDataLayoutPosition={props.bringToDataLayoutPosition} />
        ) : (
          <RequiredFieldDropdown bringToFieldPosition={props.bringToFieldPosition} bringToDataLayoutPosition={props.bringToDataLayoutPosition} />
        )}
        {savedPublicForm?.lastSaveDate && <div className="last-saved">Last Saved: {moment(savedPublicForm.lastSaveDate).format('M/D/YYYY h:mm:ss a')}</div>}
        {props.allowSave && savedPublicForm?.saveId && <Button className="secondary" content="Save" onClick={savePublicForm} loading={loading} />}
        {props.allowSave && !savedPublicForm?.saveId && (
          <>
            <div className="item">
              <Popup
                className="save-public-form"
                on="click"
                open={savePopupOpen}
                onOpen={() => {
                  setSavePopupOpen(true);
                }}
                onClose={() => {
                  setSavePopupOpen(false);
                }}
                position={popupPosition}
                trigger={<Button className="secondary" content="Save" />}
                content={
                  <>
                    <div className="save-public-form-header">Enter your email to save your progress</div>
                    <div className="save-public-form-description">
                      You'll receive a link, which you can access to finish filling out this Form later. The link will expire in 30 days.
                    </div>
                    <div className="save-public-form-input">
                      <Input
                        value={emailAddress}
                        placeholder="Email address"
                        onChange={onEmailAddressChange}
                        onBlur={validateEmailAddress}
                        error={emailError}
                        disabled={!!recipientEmailAddress}
                      />
                    </div>
                    <div className="save-public-form-footer">
                      <div className="save-public-form-terms">
                        By clicking Confirm, you are agreeing with GoFormz&nbsp;
                        <a href="https://www.goformz.com/terms" target="_blank">
                          Terms of Service.
                        </a>
                      </div>
                      <Button className="primary save-public-form-confirm" onClick={savePublicForm} disabled={!validEmail} loading={loading}>
                        Confirm
                      </Button>
                    </div>
                  </>
                }
              />
            </div>
          </>
        )}
        <div className="item">
          <Button
            className="primary"
            content="Submit"
            loading={props.isSaving}
            onClick={props.confirmSubmitSharedForm}
            disabled={props.isFetching || props.isSaving || !props.initialCalculationsHaveRun || hasMissingRequiredFields}
            icon="check"
          />
        </div>
      </div>
    </div>
  );
}
